import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InkButton } from '../components/InkButton';
import { InkedTitle } from '../components/InkedTitle';
import { Reveal } from '../components/Reveal';
import { useBooking } from '../context/BookingContext';
import { studio } from '../data';
import { useLang } from '../hooks/useLang';
import { cn } from '../lib/cn';

function sequenceLabel(step: number, lang: 'it' | 'en') {
  const it = ['Si scrive', 'Si parla', 'Si disegna', 'Si fa'];
  const en = ['Write', 'Talk', 'Draw', 'Make'];
  return (lang === 'en' ? en : it)[step - 1] ?? '';
}

export function StudioPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { openBooking } = useBooking();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${studio.address.street}, ${studio.address.zip} ${studio.address.city}`,
  )}`;

  return (
    <>
      <section className="px-6 pt-36 pb-12 md:px-10 md:pt-48 md:pb-20">
        <div className="mx-auto max-w-[1800px]">
          <p className="text-base font-display lowercase text-ink-muted tracking-tight">
            {t('studio.headerEyebrow').toLowerCase()}
          </p>
          <InkedTitle
            as="h1"
            immediate
            delay={0.15}
            className="mt-6 font-display tracking-display leading-[0.85] text-[clamp(3.5rem,13vw,14rem)] lowercase"
          >
            {t('studio.headerTitle')}
          </InkedTitle>
        </div>
      </section>

      <section className="px-6 py-12 md:px-10 md:py-20 border-t border-ink-line/40">
        <div className="mx-auto max-w-[1800px] grid gap-10 md:grid-cols-12">
          <Reveal className="md:col-span-3">
            <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
              {t('studio.ethosLabel')}
            </p>
          </Reveal>
          <Reveal className="md:col-span-9" delay={0.05}>
            <p className="text-xl md:text-3xl text-ink-paper leading-tight max-w-4xl">
              {studio.ethos[lang]}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-12 md:px-10 md:py-20 border-t border-ink-line/40">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4 grid gap-8 content-start">
            <div>
              <p className="text-sm font-display lowercase text-ink-muted tracking-tight">
                {t('studio.addressLabel')}
              </p>
              <p className="mt-4 text-lg md:text-xl">
                {studio.address.street}
                <br />
                {studio.address.zip} {studio.address.city}
              </p>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-base font-display lowercase tracking-display text-ink-paper hover:text-ink-paper/70 transition-colors"
              >
                {t('studio.viewOnMap')} ↗
              </a>
            </div>

            <div>
              <p className="text-sm font-display lowercase text-ink-muted tracking-tight">
                {t('studio.hoursLabel')}
              </p>
              <dl className="mt-4 grid gap-2">
                {studio.hours.map((h) => (
                  <div key={h.days.it} className="flex items-baseline justify-between gap-4">
                    <dt className="text-base">{h.days[lang]}</dt>
                    <dd className="font-mono text-[13px] text-ink-paper/80">
                      {h.time[lang]}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div>
              <p className="text-sm font-display lowercase text-ink-muted tracking-tight">
                {t('studio.contactLabel')}
              </p>
              <ul className="mt-4 grid gap-2 text-base">
                <li>
                  <a
                    href={`mailto:${studio.email}`}
                    className="hover:text-ink-paper transition-colors"
                  >
                    {studio.email}
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${studio.phone.replace(/\s/g, '')}`}
                    className="hover:text-ink-paper transition-colors"
                  >
                    {studio.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={`https://instagram.com/${studio.instagram}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-ink-paper transition-colors"
                  >
                    @{studio.instagram} ↗
                  </a>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal className="md:col-span-8" delay={0.1}>
            <div className="aspect-[4/3] relative overflow-hidden bg-ink-line">
              <img
                src="https://images.unsplash.com/photo-1542744383-8c330d91f4b1?w=2000&q=80&auto=format&fit=crop"
                alt={studio.brand}
                className="h-full w-full object-cover ink-photo"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-16 md:px-10 md:py-24 border-t border-ink-line/40">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
              {t('studio.process')}
            </p>
          </div>
          <div className="md:col-span-9 grid gap-px bg-ink-line/40">
            {studio.process.map((step, i) => (
              <Reveal key={step.step} delay={i * 0.06}>
                <article className="bg-ink-base grid gap-4 md:grid-cols-12 items-baseline py-8">
                  <div className="md:col-span-2 font-display text-xl lowercase tracking-display text-ink-paper/55">
                    {sequenceLabel(step.step, lang).toLowerCase()}
                  </div>
                  <h3 className="md:col-span-3 font-display tracking-display text-3xl md:text-4xl lowercase">
                    {step.title[lang]}
                  </h3>
                  <p className="md:col-span-7 text-base md:text-lg text-ink-paper/80 leading-relaxed">
                    {step.body[lang]}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-10 md:py-24 border-t border-ink-line/40">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-3">
            <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
              {t('studio.faq')}
            </p>
          </div>
          <div className="md:col-span-9">
            <ul className="grid gap-px bg-ink-line/40">
              {studio.faq.map((item, i) => {
                const open = openFaq === i;
                return (
                  <li key={i} className="bg-ink-base">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      aria-expanded={open}
                      aria-controls={`faq-${i}`}
                      className="w-full flex items-start justify-between gap-6 py-6 text-left group"
                    >
                      <span className="font-display tracking-display text-xl md:text-3xl lowercase leading-tight">
                        {item.q[lang]}
                      </span>
                      <span
                        aria-hidden
                        className={cn(
                          'shrink-0 size-8 border border-ink-line/60 group-hover:border-ink-paper flex items-center justify-center transition-transform duration-300',
                          open && 'rotate-45 border-ink-paper',
                        )}
                      >
                        +
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          id={`faq-${i}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="pb-6 pr-12 text-base md:text-lg text-ink-paper/80 leading-relaxed max-w-3xl">
                            {item.a[lang]}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      <section className="relative bg-ink-paper text-ink-base px-6 py-24 md:px-10 md:py-36 overflow-hidden">
        <Reveal>
          <h2 className="font-display tracking-display leading-[0.88] text-[clamp(3rem,11vw,11rem)] lowercase">
            {t('hero.cta')}.
          </h2>
          <div className="mt-10">
            <InkButton
              as="button"
              tone="dark"
              size="lg"
              onClick={() => openBooking()}
            >
              {t('booking.fields.submit')}
            </InkButton>
          </div>
        </Reveal>
      </section>
    </>
  );
}
